import * as Icons from "@/components/Icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ThemeSelect } from "./Theme";
import { logout, useAuth, getStoredAccounts, switchAccount, removeAccountFromStorage } from "@/hooks/auth";
import { DialogMenu } from "./Menu";
import { ChevronDown, Check, Plus, X } from "lucide-react";
import logoImage from "@root/public/images/logo-favicon.png";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { JourneyTimer } from "@/components/JourneyTimer";
// import { Notifications } from "./Notifications";

export default function Header() {
  const auth = useAuth();
  const redirect = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const storedAccounts = getStoredAccounts();

  const currentEmail = auth?.user?.email;

  const handleSwitchAccount = (email: string) => {
    if (email !== currentEmail) {
      switchAccount(email);
    }
  };

  const handleAddAccount = () => {
    setIsOpen(false);
    redirect("/login?addAccount=true");
  };

  const handleRemoveAccount = (e: React.MouseEvent, email: string) => {
    e.stopPropagation();
    if (email === currentEmail) {
      const otherAccounts = storedAccounts.filter((acc) => acc.user?.email !== email);
      if (otherAccounts.length > 0) {
        removeAccountFromStorage(email);
        switchAccount(otherAccounts[0].user?.email);
      } else {
        logout();
        redirect("/login");
      }
    } else {
      removeAccountFromStorage(email);
    }
  };

  return (
    <div className="h-header w-full">
      <header className="h-header border-b px-6 py-3 z-50 bg-transparent">
        <div className="flex justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <DialogMenu />
            <Link
              to="/"
              className="p-1 rounded bg-zinc-100 block md:hidden border"
            >
              <img
                className="size-9 object-cover aspect-square"
                src={logoImage}
              />
            </Link>
          </div>

          <div className="gap-5 flex items-end">
            <JourneyTimer />
            {/* <Notifications /> */}
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <PopoverTrigger asChild>
                <div className="flex gap-3 items-center cursor-pointer shadow bg-zinc-50 rounded-xl py-1 px-1 dark:bg-[#2c2c2c] hover:bg-zinc-100 dark:hover:bg-[#3c3c3c] transition-colors">
                  <img
                    src={auth?.user?.photo || logoImage}
                    alt="avatar"
                    className="aspect-square size-9 object-fill rounded-xl border"
                  />
                  <div className="flex flex-col justify-center translate-y-0.5">
                    <span className="text-sm font-medium">
                      {auth?.user?.name || "Admin"}
                    </span>
                    <small className="text-xs -translate-y-1 font-normal text-slate-600 dark:text-slate-300">
                      {auth?.user?.email}
                    </small>
                  </div>
                  <ChevronDown className="mx-2 h-4 w-4 text-slate-500" />
                </div>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                className="flex flex-col p-0 w-72"
              >
                {/* Lista de contas */}
                <div className="p-2">
                  <p className="text-xs text-slate-500 px-2 py-1 font-medium">Contas</p>
                  <div className="space-y-1">
                    {storedAccounts.map((account) => (
                      <div
                        key={account.user?.email}
                        onClick={() => handleSwitchAccount(account.user?.email)}
                        className={cn(
                          "flex items-center gap-3 p-2 rounded-lg cursor-pointer group",
                          account.user?.email === currentEmail
                            ? "bg-primary/10"
                            : "hover:bg-slate-100 dark:hover:bg-slate-800"
                        )}
                      >
                        <img
                          src={account.user?.photo || logoImage}
                          alt="avatar"
                          className="aspect-square size-9 object-fill rounded-full border"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {account.user?.name || "Usuário"}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {account.user?.email}
                          </p>
                          {account.impersonation && (
                            <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                              acesso admin
                            </span>
                          )}
                        </div>
                        {account.user?.email === currentEmail ? (
                          <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        ) : (
                          <button
                            onClick={(e) => handleRemoveAccount(e, account.user?.email)}
                            className="h-6 w-6 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-100 dark:hover:bg-red-900/30 transition-opacity"
                          >
                            <X className="h-3 w-3 text-red-500" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <Separator className="w-full" />

                {/* Adicionar conta */}
                <div className="p-2">
                  <Button
                    variant="ghost"
                    className="flex justify-start gap-2 px-2 w-full text-stone-900 hover:bg-stone-950/10 hover:dark:bg-zinc-800/80 dark:text-[#F4F4F5]"
                    onClick={handleAddAccount}
                  >
                    <Plus className="h-4 w-4" /> Adicionar conta
                  </Button>
                </div>

                <Separator className="w-full" />

                {/* Tema */}
                <ThemeSelect />

                <Separator className="w-full" />

                {/* Logout */}
                <div className="p-2 w-full">
                  <Button
                    variant="ghost"
                    className="flex justify-start gap-2 px-2 w-full text-stone-900 hover:bg-stone-950/10 hover:dark:bg-zinc-800/80 dark:text-[#F4F4F5]"
                    onClick={() => {
                      logout();
                      redirect("/login");
                    }}
                  >
                    <Icons.ArrowEnd /> Sair desta conta
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </header>
    </div>
  );
}
